import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { AuthProvider } from './contexts/AuthContext';
import ResponsiveNavigation from './components/ResponsiveNavigation';
import Login from './components/Login';
import Dashboard from './components/dashboard/Dashboard';
import MenuManagement from './components/menu/MenuManagement';
import ProtectedRoute from './components/ProtectedRoute';

// Import i18n configuration
import './i18n';
import ShiftManagement from './components/shifts/ShiftManagement';
import UserManagement from './components/users/UserManagement';
import OrganisationProfile from './components/organisation/OrganisationProfile';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4a90e2', // Blue theme for organization portal
    },
    secondary: {
      main: '#4ecdc4', // Same accent color as employee app
    },
    background: {
      default: '#f7f7f7',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
        },
      },
    },
  },
});

function App() {
  // Get your client ID from environment variable if possible
  const GOOGLE_CLIENT_ID = "642033309304-f097g06sq3qf2bbt7c6m0k1o691ceglg.apps.googleusercontent.com";

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <Router>
            <AuthProvider>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <ResponsiveNavigation />
                <Box
                  component="main"
                  sx={{
                    flexGrow: 1,
                    width: '100%',
                    mt: {
                      xs: '56px', // Mobile AppBar height
                      sm: '64px'  // Desktop AppBar height
                    },
                    px: {
                      xs: 2,
                      sm: 3
                    },
                    pt: 3,
                    pb: 4,
                  }}
                >
                  <Container
                    maxWidth="lg"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      margin: '0 auto'
                    }}
                  >
                    <Routes>
                      {/* Public routes */}
                      <Route path="/login" element={<Login />} />
                      
                      {/* Protected routes */}
                      <Route element={<ProtectedRoute />}>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/menus" element={<MenuManagement />} />
                        {/* Add routes for shift and user management */}
                        <Route path="/shifts" element={<ShiftManagement />} />
                        <Route path="/users" element={<UserManagement />} />
                        <Route path="/profile" element={<OrganisationProfile />} />
                        {/* Other routes can be uncommented as they're implemented:
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/menus/edit/:id" element={<MenuEdit />} /> 
                        <Route path="/menus/view/:id" element={<MenuView />} />
                        */}
                      </Route>
                      
                      {/* Fallback */}
                      <Route path="*" element={<Navigate to="/login" replace />} />
                    </Routes>
                  </Container>
                </Box>
              </Box>
            </AuthProvider>
          </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;